import { ApiSchema } from "@api-types";
import { ObservableOf } from "@rsm";
import { FiltersHelper } from "helpers/filtersHelper";
import { map } from "rxjs";
import { odataAct, odataFuncList, odataFuncOne, odataGet, odataList } from "services/odataApi";
import { Globals } from "types/globals";
import { ValuesType } from "utility-types";

export namespace ResultsModuleRequests {
  export const getProject$ = (projectId: number) =>
    odataGet("getProjectsOne", { key: projectId })
      .select(
        "id",
        "projectName",
        "launchDate",
        "productType",
        "projectType",
        "statusCode",
        "projectTypeId",
        "scheduleWavesRequired",
        "scheduleFrequencyWeeks",
        "scheduleStartDate",
        "wavesRequired",
        "schedulePause",
        "createdAt",
        "category",
        "projectQuestions",
        "splitCellQuota",
        "activeProjectObjectiveId",
        "isBranded",
        "frameworkId",
        "researchNote",
        "internalResearchNote",
        "projectGroupId",
        "brand",
      )
      .expand("projectsRegions", (z) =>
        z
          .select("regionId")
          .filter("regionId", "lt", Globals.RegionOther)
          .expand("region", (a) => a.select("regionName", "currencyCode", "currencySymbol")),
      )
      .expandAndSelect(
        "projectWeeks",
        (s) => s.filter("surveyRunId", "ne", null),
        "id",
        "surveyRunId",
        "regionId",
        "week",
        "scheduleDate",
        "surveySampleProviderId",
      )
      .expandAndSelect(
        "projectSchedules",
        (ps) =>
          ps
            .expandAndSelect("creditLog", null, "id", "creditAmount")
            .expandAndSelect("region", null, "id", "regionName"),
        "id",
        "regionId",
        "surveyRunId",
        "scheduleDate",
        "surveyProcessingId",
      )
      .expandAndSelect("projectPriceSensitivities", null, "id")
      .expandAndSelect(
        "marketReach",
        (mr) => mr.expandAndSelect("marketReachesTestOptions", null, "testOptionId"),
        "id",
      )
      .expandAndSelect("projectGroupTags", null, "id", "name")
      .expand("projectQuestions", (z) =>
        z.select("id", "questionPositionId", "questionId", "projectQuestionThemingVersionId"),
      )
      .expandAndSelect("activeProjectObjective", null, "summary")
      .expandAndSelect(
        "framework",
        (f) => f.expandAndSelect("standardQuestions", null, "id", "questionLabel", "questionTypeId"),
        "objectiveSummaries",
        "standardQuestions",
        "title",
      )
      .expandAndSelect("projectGroup", null, "name")
      .expandAndSelect(
        "testOptionCells",
        (z) => z.expandAndSelect("testOptions", null, "id"),
        "id",
        "numTestOptionsShown",
      )
      .$();

  export type Project = ObservableOf<ReturnType<typeof getProject$>>;

  export const getQuestions$ = (projectId: number) =>
    odataList("getProjectQuestions")
      .select(
        "id",
        "questionPositionId",
        "sortOrder",
        "questionId",
        "standardQuestionId",
        "testOptionId",
        "projectQuestionThemingVersionId",
      )
      .orderBy("sortOrder")
      .expandAndSelect(
        "routing",
        (r) =>
          r
            .expandAndSelect("routingConditions", (rc) =>
              rc.expandAndSelect("questionOptions", null, "optionText"),
            )
            .expandAndSelect("projectQuestions", null, "id"),
        "id",
        "projectQuestionId",
        "routingConditions",
        "projectQuestions",
      )
      .expandAndSelect("questionTexts", null, "questionText")
      .expandAndSelect(
        "question",
        (q) =>
          q
            .expandAndSelect(
              "s3File",
              (s) =>
                s.expandAndSelect(
                  "thumbnails",
                  null,
                  "squareThumbnailSignedUrl",
                  "ratioThumbnailSignedUrl",
                  "squareThumbnailFileType",
                  "ratioThumbnailFileType",
                  "fileType",
                ),
              "key",
              "fileType",
              "signedUrl",
            )
            .expandAndSelect("targets", (a) => a.filter("projectId", "eq", projectId), "questionOptionId")
            .expandAndSelect(
              "questionOptions",
              (a) =>
                a.expandAndSelect(
                  "s3File",
                  (s) =>
                    s.expandAndSelect(
                      "thumbnails",
                      null,
                      "squareThumbnailSignedUrl",
                      "ratioThumbnailSignedUrl",
                      "squareThumbnailFileType",
                      "ratioThumbnailFileType",
                      "fileType",
                    ),
                  "key",
                  "fileType",
                  "signedUrl",
                ),
              "id",
              "optionText",
              "exclusive",
              "isExcluded",
            ),
        "questionText",
        "questionTitle",
        "questionTypeId",
        "questionOptions",
        "targets",
        "standardQuestionId",
        "marketReachEnabled",
      )
      .orderBy("questionPositionId")
      .filter("projectId", "eq", projectId)
      .$()
      .pipe(map((c) => c.value));

  export type ProjectQuestion = ValuesType<ObservableOf<ReturnType<typeof getQuestions$>>>;

  export const getTestOptions$ = (projectId: number) =>
    odataList("getTestOptions")
      .select("id", "conceptText", "conceptDescription", "projectId", "createdAt")
      .expandAndSelect(
        "s3File",
        (s) =>
          s.expandAndSelect(
            "thumbnails",
            null,
            "squareThumbnailSignedUrl",
            "ratioThumbnailSignedUrl",
            "squareThumbnailFileType",
            "ratioThumbnailFileType",
            "fileType",
          ),
        "key",
        "fileType",
        "signedUrl",
      )
      .filter("projectId", "eq", projectId)
      .orderBy("id")
      .$()
      .pipe(map((c) => c.value));
  export type TestOption = ValuesType<ObservableOf<ReturnType<typeof getTestOptions$>>>;

  export const getSegments$ = (projectId: number) =>
    odataList("getSegments")
      .filter("projectId", "eq", projectId)
      .expand("aiProjectSummaries", (z) => z.select("id").top(0).count())
      .select("id", "name", "responsesFilters")
      .$()
      .pipe(map((v) => v.value));
  export type Segment = ValuesType<ObservableOf<ReturnType<typeof getSegments$>>>;

  export const getSchedulingState$ = (projectId: number) =>
    odataFuncOne("funcProjectsSchedulingState", { key: projectId }).$();
  export type SchedulingState = ValuesType<ObservableOf<ReturnType<typeof getSchedulingState$>>>;

  export const getSystemQuestionOptionReqBuilder$ = (
    selectedRegionId: number,
    selectedSurveyRunIds: number[] | null | undefined,
    projectId: number,
  ) => {
    const builder = odataFuncList("funcSystemQuestionOptionProjectsDemographics", {
      projectIds: [projectId],
      regionId: selectedRegionId,
      surveyRunIds: selectedSurveyRunIds ?? undefined,
    });
    return builder.$().pipe(map((z) => z.value));
  };

  export const getProjectGroupSegments$ = (projectGroupId: number) =>
    odataList("getProjectGroupSegments")
      .filter("projectGroupId", "eq", projectGroupId)
      .filter("isCategorySegment", "eq", true)
      .$()
      .pipe(map((z) => z.value));
  export type ProjectGroupSegment = ValuesType<ObservableOf<ReturnType<typeof getProjectGroupSegments$>>>;

  export const getStandardQuestionData$ = (
    projectId: number,
    regionIds: number[],
    testOptionId?: number,
    filters?: {
      [x: string]: number[];
    },
    questionOptions?: {
      [x: number]: {
        [x: number]: boolean;
      };
    },
    selectedPanelType?: ApiSchema.PanelType,
    categorySegmentIds?: number[],
  ) => {
    const filterIds = Object.values(filters ?? {}).flat();
    const includeIds = FiltersHelper.filterSubDictionaryByValue(questionOptions ?? {}, true)
      .map((i) => i.ids)
      .flat();
    const excludeIds = FiltersHelper.filterSubDictionaryByValue(questionOptions ?? {}, false)
      .map((i) => i.ids)
      .flat();
    const breakdownBy: ApiSchema.BreakdownBy[] = ["ByAnswerValue"];
    if (testOptionId) {
      breakdownBy.push("ByTestOption");
    }
    return odataFuncList("funcResponsesQualResults", {
      filters: {
        testOptionIds: testOptionId ? [testOptionId] : undefined,
        standardQuestionIds: [
          Globals.StandardQuestions.Desire,
          Globals.StandardQuestions.BrandFit,
          Globals.StandardQuestions.PurchaseIntent,
          Globals.StandardQuestions.PurchaseIntentV2,
          Globals.StandardQuestions.Distinctiveness,
          Globals.StandardQuestions.Likeability,
          Globals.StandardQuestions.BrandRecognition,
          Globals.StandardQuestions.Consideration,
          Globals.StandardQuestions.Believability,
        ], //1, 2, 3, 4, 5, 6, 23
        regionIds: regionIds,

        filterIds: filterIds.length ? filterIds : undefined,
        questionOptionsIds: includeIds?.length ? includeIds : undefined,
        excludeQuestionOptionsIds: excludeIds?.length ? excludeIds : undefined,
        panelType: selectedPanelType ?? undefined,
        projectGroupSegmentIds: categorySegmentIds?.length ? categorySegmentIds : undefined,
      },
      breakdownBy,
      projectId: projectId,
      includeFullResults: true,
    })
      .$()
      .pipe(
        map((a) =>
          a?.value.sort(
            (a, b) =>
              Globals.getStandardQuestionOrder(a.standardQuestionId ?? 0) -
              Globals.getStandardQuestionOrder(b.standardQuestionId ?? 0),
          ),
        ),
      );
  };

  export type StandardQuestionData = ValuesType<ObservableOf<ReturnType<typeof getStandardQuestionData$>>>;

  export const createNarrative$ = (projectId: number) =>
    odataAct("actProjectsCreateNarrative", {}, { key: projectId }).$();
}
